import OpenAI from 'openai';

export interface VocabularyTermExample {
  type?: string;
  english?: string;
  translation?: string | Record<string, string>;
  [key: string]: unknown;
}

export interface VocabularyTerm {
  term?: string;
  explanation?: string | Record<string, string>;
  examples?: VocabularyTermExample[];
  chosen_explanation?: string | Record<string, string>;
  chosen_explanation_audio?: string | Record<string, string>;
  [key: string]: unknown;
}

export interface VocabularyScenario {
  name?: string;
  terms?: VocabularyTerm[];
  [key: string]: unknown;
}

export interface VocabularyPayload {
  scenarios?: VocabularyScenario[];
  [key: string]: unknown;
}

export interface ExampleToTranslate {
  index: number;
  english: string;
}

export interface TermPromptItem {
  id: string;
  term: string;
  scenario: string;
  example: string;
  translation?: string;
  explanation?: string;
  // Examples that need translation for the target language
  examplesToTranslate?: ExampleToTranslate[];
}

export interface ChosenExplanationOptions {
  openaiClient?: OpenAI;
  openaiApiKey?: string;
  batchSize?: number;
  maxAttempts?: number;
  logger?: Pick<typeof console, 'log' | 'warn' | 'error'>;
  targetLanguage?: string;
  targetLanguageCode?: string;
}

interface ExplanationResult {
  chosen: string;
  explanation: string;
  // Translations for examples, keyed by example index
  exampleTranslations?: Record<number, string>;
}

export interface ChosenExplanationResult {
  vocabulary: VocabularyPayload;
  promptItemCount: number;
  generatedCount: number;
  explanationMap: Map<string, ExplanationResult>;
}

const MAX_EXAMPLE_LENGTH = 260;
const DEFAULT_BATCH_SIZE = 5;  // Reduced from 10 to avoid token limit issues
const DEFAULT_MAX_ATTEMPTS = 3;

export class ChosenExplanationError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ChosenExplanationError';
    this.statusCode = statusCode;
  }
}

const EXPLANATION_PREFIX_PATTERN =
  /^(?:直接解释|场景解释|替换解释|语气解释|Direct Explanation|Scenario Explanation|Alternative Explanation|Tone Explanation|직접 설명|상황 설명|대체 설명|어조 설명)\s*[:：]\s*/u;
const LINE_BREAK_PATTERN = /[\r\n\u2028\u2029]+/g;
const MULTIPLE_SPACE_PATTERN = /\s{2,}/g;

export function normalizeChosenExplanationText(
  value?: string | Record<string, string> | null,
  languageCode: string = 'zh'
): string {
  if (!value) {
    return "";
  }

  let rawText = "";

  if (typeof value === 'string') {
    // For legacy string format, only return if languageCode is 'zh' (Chinese)
    // Otherwise, we consider it as "missing explanation" for that language
    if (languageCode === 'zh') {
      rawText = value;
    } else {
      return ""; 
    }
  } else if (typeof value === 'object') {
    // Try to find the exact match first
    rawText = value[languageCode]; 
    // Maybe fallback to short code if regional code provided (e.g. 'zh-CN' -> 'zh')
    if (!rawText && languageCode.includes('-')) {
        const shortCode = languageCode.split('-')[0];
        rawText = value[shortCode];
    }
    // STRICT MODE: Do NOT fallback to 'zh', 'en' or arbitrary values.
    // If the requested language is missing, return empty string to indicate missing data.
  }

  if (!rawText) return "";

  let cleaned = rawText
    .replace(EXPLANATION_PREFIX_PATTERN, "")
    .replace(LINE_BREAK_PATTERN, " ")
    .replace(/\u3000+/g, " ");

  // Ensure punctuation is spaced consistently for readability
  cleaned = cleaned
    .replace(/([。！？；])\s*/g, "$1 ")
    .replace(/([,，;；])(?=\S)/g, "$1 ")
    .replace(/([：:])\s*/g, "$1 ")
    .replace(MULTIPLE_SPACE_PATTERN, " ")
    .trim();

  return cleaned;
}

export function normalizeChosenExplanationAudioUrl(
  value?: string | Record<string, string> | null,
  languageCode: string = 'zh'
): string {
  if (!value) {
    return "";
  }

  if (typeof value === 'string') {
    // Legacy string format. Usually corresponds to 'zh' or 'en' (whatever was generated first).
    // For audio, we might want to be lenient and return it if we can't find a specific one,
    // OR strict like text.
    // Given the user feedback: "hearing Korean when I want Chinese", strict is better.
    // BUT, if existing data is string, and we upgrade, we lose access to it for other languages?
    // Let's assume legacy string is 'zh' if we must guess, or just return it as a fallback?
    // User said: "db has string audio".
    // If we return string for 'ko', user hears wrong audio.
    // So strict mode is safer: return empty if mismatch.
    if (languageCode === 'zh') {
        return value;
    }
    // If we are 'ko', and value is string, we assume it's NOT ko (since new gen uses object).
    // So return empty -> triggers generation.
    return "";
  } else if (typeof value === 'object') {
    let url = value[languageCode];
    if (!url && languageCode.includes('-')) {
        url = value[languageCode.split('-')[0]];
    }
    return url || "";
  }

  return "";
}

function pickExample(term: VocabularyTerm | undefined): VocabularyTermExample | null {
  if (!term?.examples || term.examples.length === 0) {
    return null;
  }

  const originalExample = term.examples.find((example) => example?.type === 'original' && example.english);
  if (originalExample?.english) {
    return originalExample;
  }

  const fallbackExample = term.examples.find((example) => example?.english);
  return fallbackExample ?? null;
}

function truncateExampleText(text: string): string {
  if (text.length <= MAX_EXAMPLE_LENGTH) {
    return text;
  }
  return `${text.slice(0, MAX_EXAMPLE_LENGTH - 1)}…`;
}

// Helper to check if example translation exists for target language
function hasExampleTranslation(example: VocabularyTermExample, targetLanguageCode: string): boolean {
  const translation = example.translation;
  if (!translation) return false;
  
  if (typeof translation === 'string') {
    // Legacy string format - assume it's Chinese
    return targetLanguageCode === 'zh';
  }
  
  if (typeof translation === 'object') {
    return !!translation[targetLanguageCode];
  }
  
  return false;
}

export function buildChosenExplanationPromptItems(
  vocabulary: VocabularyPayload,
  targetLanguageCode?: string
): TermPromptItem[] {
  if (!vocabulary?.scenarios) {
    return [];
  }

  const items: TermPromptItem[] = [];

  vocabulary.scenarios.forEach((scenario, scenarioIndex) => {
    if (!scenario?.terms) return;

    scenario.terms.forEach((term, termIndex) => {
      if (!term?.term) return;

      // Check if chosen_explanation already exists for target language
      let needsChosenExplanation = true;
      if (targetLanguageCode && term.chosen_explanation) {
        const existingText = normalizeChosenExplanationText(term.chosen_explanation, targetLanguageCode);
        if (existingText) {
          needsChosenExplanation = false;
        }
      }

      // Collect examples that need translation
      const examplesToTranslate: ExampleToTranslate[] = [];
      if (targetLanguageCode && term.examples) {
        term.examples.forEach((example, exampleIndex) => {
          if (example.english && !hasExampleTranslation(example, targetLanguageCode)) {
            examplesToTranslate.push({
              index: exampleIndex,
              english: truncateExampleText(example.english.trim())
            });
          }
        });
      }

      // If both chosen_explanation exists AND all examples have translations, skip this term
      if (!needsChosenExplanation && examplesToTranslate.length === 0) {
        return;
      }

      const example = pickExample(term);
      if (!example?.english) return;

      const trimmedExample = truncateExampleText(example.english.trim());
      if (!trimmedExample) return;
      
      // Handle normalized explanation string for prompt if necessary
      let explanationStr = "";
      if (typeof term.explanation === 'string') {
          explanationStr = term.explanation;
      } else if (term.explanation && typeof term.explanation === 'object') {
          explanationStr = term.explanation['en'] || term.explanation['english'] || Object.values(term.explanation)[0] || "";
      }

      let translationStr = "";
      if (typeof example.translation === 'string') {
        translationStr = example.translation;
      } else if (example.translation && typeof example.translation === 'object') {
        const transObj = example.translation as Record<string, string>;
        translationStr = transObj[targetLanguageCode || 'zh'] || transObj['zh'] || Object.values(transObj)[0] || "";
      }

      items.push({
        id: `${scenarioIndex}:${termIndex}`,
        term: term.term,
        scenario: scenario?.name || '',
        example: trimmedExample,
        translation: translationStr.trim(),
        explanation: explanationStr.trim(),
        examplesToTranslate: examplesToTranslate.length > 0 ? examplesToTranslate : undefined,
      });
    });
  });

  return items;
}

function mergeChosenExplanations(
  vocabulary: VocabularyPayload,
  responses: Map<string, ExplanationResult>,
  languageCode: string
): VocabularyPayload {
  if (!vocabulary?.scenarios) {
    return vocabulary;
  }

  const updatedScenarios = vocabulary.scenarios.map((scenario, scenarioIndex) => {
    if (!scenario?.terms) {
      return scenario;
    }

    const updatedTerms = scenario.terms.map((term, termIndex) => {
      const id = `${scenarioIndex}:${termIndex}`;
      const generated = responses.get(id);

      if (!generated) {
        return term;
      }

      // 1. Handle Chosen Explanation
      let cleanedChosen = generated.chosen
        .replace(EXPLANATION_PREFIX_PATTERN, "")
        .replace(LINE_BREAK_PATTERN, " ")
        .replace(/\u3000+/g, " ");

      cleanedChosen = cleanedChosen
        .replace(/([。！？；])\s*/g, "$1 ")
        .replace(/([,，;；])(?=\S)/g, "$1 ")
        .replace(/([：:])\s*/g, "$1 ")
        .replace(MULTIPLE_SPACE_PATTERN, " ")
        .trim();

      const normalizedChosen = cleanedChosen || generated.chosen;

      const existingChosen = term.chosen_explanation;
      let newChosenObj: Record<string, string> = {};

      if (typeof existingChosen === 'string') {
        newChosenObj['zh'] = existingChosen;
      } else if (existingChosen && typeof existingChosen === 'object') {
        newChosenObj = { ...existingChosen };
      }
      newChosenObj[languageCode] = normalizedChosen;

      // 2. Handle Standard Explanation
      const normalizedExplanation = generated.explanation.trim();
      const existingExplanation = term.explanation;
      let newExplanationObj: Record<string, string> = {};

      if (typeof existingExplanation === 'string') {
        newExplanationObj['zh'] = existingExplanation;
      } else if (existingExplanation && typeof existingExplanation === 'object') {
        newExplanationObj = { ...existingExplanation };
      }
      
      if (normalizedExplanation) {
          newExplanationObj[languageCode] = normalizedExplanation;
      }

      // 3. Handle Example Translations
      let updatedExamples = term.examples;
      if (generated.exampleTranslations && term.examples) {
        updatedExamples = term.examples.map((example, exampleIndex) => {
          const newTranslation = generated.exampleTranslations?.[exampleIndex];
          if (!newTranslation) {
            return example;
          }

          // Merge translation into multi-language object
          const existingTranslation = example.translation;
          let newTranslationObj: Record<string, string> = {};

          if (typeof existingTranslation === 'string') {
            newTranslationObj['zh'] = existingTranslation;
          } else if (existingTranslation && typeof existingTranslation === 'object') {
            newTranslationObj = { ...existingTranslation };
          }
          newTranslationObj[languageCode] = newTranslation.trim();

          return {
            ...example,
            translation: newTranslationObj,
          };
        });
      }

      return {
        ...term,
        chosen_explanation: newChosenObj,
        explanation: newExplanationObj,
        examples: updatedExamples,
      };
    });

    return {
      ...scenario,
      terms: updatedTerms,
    };
  });

  return {
    ...vocabulary,
    scenarios: updatedScenarios,
  };
}

async function requestBatch(
  client: OpenAI,
  items: TermPromptItem[],
  logger: Pick<typeof console, 'log' | 'warn' | 'error'>,
  targetLanguage: string
): Promise<Map<string, ExplanationResult>> {
  const promptPayload = JSON.stringify({ terms: items }, null, 2);
  const termById = new Map(items.map((item) => [item.id, item.term?.trim() ?? '']));

  const systemPrompt = `You are a bilingual teaching assistant for English learners. You need to generate a short, natural, and spoken "chosen_explanation" in ${targetLanguage} AND a standard "explanation_translation" in ${targetLanguage} for a given vocabulary term. You will receive a terms array, where each element contains id, term, example, etc. Generate the most suitable explanation for each item and return it as a JSON object with the following structure: {"results":[{"id":"{id}","term":"{term}","chosen_explanation":"...", "explanation_translation":"..."}]}`;

  let instructions = '';
  if (targetLanguage === 'Chinese' || targetLanguage === '中文') {
    instructions = `请根据我提供的英文例句中的 English 部分，找出里面的重点词汇 term，并结合语境只选择下面四种解释方式之一来说明这个词（作为 chosen_explanation）。同时提供该词的标准中文释义（作为 explanation_translation）。
    
四种 chosen_explanation 风格（仅供参考）：
1. 直接解释：hear me out 等于"先听我说完"...
2. 场景解释：想象一下你提出一个主意...
3. 替换解释：hear me out 跟 let me finish 很像...
4. 语气解释：这句话经常带有一种恳求的感觉...

要求：
- chosen_explanation 必须创造一个全新的英文例句并在后面附上${targetLanguage}翻译。
- explanation_translation 仅提供准确、简明的中文释义即可（无需例句）。
- 必须对 terms 数组里的每一项逐个生成结果。`;
  } else if (targetLanguage === 'Korean' || targetLanguage === '한국어') {
    instructions = `Provide the explanations in Korean.
    1. chosen_explanation: Choose one of the 4 styles (Direct, Scenario, Replacement, Tone). Create a BRAND NEW English example sentence followed by its Korean translation.
    2. explanation_translation: Provide a standard, concise Korean definition/translation of the term.

    Generate results for EVERY item in the terms array.`;
  } else {
    instructions = `Provide the explanations in ${targetLanguage}.
    1. chosen_explanation: Choose one of the 4 styles. Create a BRAND NEW English example sentence followed by its ${targetLanguage} translation.
    2. explanation_translation: Provide a standard, concise definition/translation of the term in ${targetLanguage}.

    Generate results for EVERY item.`;
  }

  const completion = await client.chat.completions.create({
    model: 'gpt-4o-mini',
    temperature: 0.6,
    response_format: {
      type: 'json_schema',
      json_schema: {
        name: 'chosen_explanations',
        strict: true,
        schema: {
          type: 'object',
          additionalProperties: false,
          required: ['results'],
          properties: {
            results: {
              type: 'array',
              items: {
                type: 'object',
                additionalProperties: false,
                required: ['id', 'term', 'chosen_explanation', 'explanation_translation'],
                properties: {
                  id: { type: 'string' },
                  term: { type: 'string' },
                  chosen_explanation: { type: 'string' },
                  explanation_translation: { type: 'string' },
                },
              },
            },
          },
        },
      },
    },
    messages: [
      { role: 'system', content: systemPrompt },
      {
        role: 'user',
        content: `${instructions}\n\nWe have ${items.length} items. Generate results for each one. Missing any item counts as failure.\n\nData:\n${promptPayload}`,
      },
    ],
    max_tokens: 10000,
  });

  const rawContent = completion.choices[0]?.message?.content ?? '';

  let parsed: { results?: { id?: string; term?: string; chosen_explanation?: string; explanation_translation?: string }[] };
  try {
    parsed = JSON.parse(rawContent);
  } catch (parseError) {
    logger.error('Failed to parse chosen_explanation response:', rawContent, parseError);
    throw new ChosenExplanationError('Failed to parse model response', 502);
  }

  const responseItems = Array.isArray(parsed.results) ? parsed.results : [];

  const batchMap = new Map<string, ExplanationResult>();

  responseItems.forEach((item) => {
    const id = item?.id?.trim();
    const chosen = item?.chosen_explanation?.trim();
    const explanation = item?.explanation_translation?.trim();

    if (id && chosen && explanation) {
      const term = termById.get(id);
      const sanitizedChosen = term ? chosen.replace(/\[\s*term\s*\]/gi, term) : chosen;
      const sanitizedExplanation = term ? explanation.replace(/\[\s*term\s*\]/gi, term) : explanation;
      
      batchMap.set(id, {
          chosen: sanitizedChosen,
          explanation: sanitizedExplanation
      });
    }
  });

  return batchMap;
}

async function generateChosenExplanationMap(
  client: OpenAI,
  items: TermPromptItem[],
  options: { batchSize: number; maxAttempts: number; logger: Pick<typeof console, 'log' | 'warn' | 'error'>, targetLanguage: string }
): Promise<Map<string, ExplanationResult>> {
  const explanationMap = new Map<string, ExplanationResult>();
  const queue = items.map((item) => ({ item, attempts: 0 }));

  while (queue.length > 0) {
    const currentBatch = queue.splice(0, options.batchSize);
    const batchItems = currentBatch.map((entry) => entry.item);

    try {
      const batchResults = await requestBatch(client, batchItems, options.logger, options.targetLanguage);

      currentBatch.forEach((entry) => {
        const generated = batchResults.get(entry.item.id);

        if (generated) {
        // Sanitize again if needed, but requestBatch already did it.
          explanationMap.set(entry.item.id, generated);
          return;
        }

        if (entry.attempts + 1 < options.maxAttempts) {
          queue.push({ item: entry.item, attempts: entry.attempts + 1 });
          options.logger.warn(
            '[chosen-explanation] retrying missing term',
            entry.item.id,
            entry.item.term,
            'attempt',
            entry.attempts + 2
          );
        } else {
          options.logger.error(
            '[chosen-explanation] failed after max attempts',
            entry.item.id,
            entry.item.term
          );
        }
      });
    } catch (batchError) {
      options.logger.error('[chosen-explanation] batch request failed', batchError);

      currentBatch.forEach((entry) => {
        if (entry.attempts + 1 < options.maxAttempts) {
          queue.push({ item: entry.item, attempts: entry.attempts + 1 });
        } else {
          options.logger.error(
            '[chosen-explanation] failed after max attempts',
            entry.item.id,
            entry.item.term
          );
        }
      });
    }
  }

  return explanationMap;
}

export async function generateChosenExplanationsForVocabulary(
  vocabulary: VocabularyPayload,
  options: ChosenExplanationOptions = {}
): Promise<ChosenExplanationResult> {
  // Pass targetLanguageCode to filter out items that already have explanations
  const promptItems = buildChosenExplanationPromptItems(vocabulary, options.targetLanguageCode);
  
  if (promptItems.length === 0) {
    // IMPORTANT: If all items are skipped (because they are already translated),
    // we should return success with the original vocabulary, not an error.
    // This prevents "No vocabulary terms found" error when everything is already up to date.
    return {
        vocabulary,
        promptItemCount: 0,
        generatedCount: 0,
        explanationMap: new Map()
    };
  }

  const logger = options.logger ?? console;

  const client =
    options.openaiClient ??
    (options.openaiApiKey ? new OpenAI({ apiKey: options.openaiApiKey }) : null);

  if (!client) {
    throw new ChosenExplanationError('OpenAI client is not configured.', 500);
  }

  const batchSize = options.batchSize ?? DEFAULT_BATCH_SIZE;
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  
  const targetLanguage = options.targetLanguage || 'Chinese';
  const targetLanguageCode = options.targetLanguageCode || 'zh'; // Default code

  const explanationMap = await generateChosenExplanationMap(client, promptItems, {
    batchSize,
    maxAttempts,
    logger,
    targetLanguage,
  });

  if (explanationMap.size === 0) {
    throw new ChosenExplanationError('Model did not return any explanations', 502);
  }

  const updatedVocabulary = mergeChosenExplanations(vocabulary, explanationMap, targetLanguageCode);

  return {
    vocabulary: updatedVocabulary,
    promptItemCount: promptItems.length,
    generatedCount: explanationMap.size,
    explanationMap,
  };
}
